import { useCallback, useEffect, useRef, useState } from "react";

export function useAudio(onEnded) {
  const ref = useRef(null);
  const endedRef = useRef(onEnded);
  endedRef.current = onEnded;
  const preloads = useRef(new Map());
  const fadeTimer = useRef(null);
  const [st, setSt] = useState({ playing: false, time: 0, dur: 0, error: false, loading: false, src: null });

  if (!ref.current && typeof Audio !== "undefined") {
    ref.current = new Audio();
    ref.current.preload = "auto";
  }

  useEffect(() => {
    const a = ref.current;
    const up = (patch) => setSt((s) => ({ ...s, ...patch }));
    const h = {
      play: () => up({ playing: true, error: false }),
      pause: () => up({ playing: false }),
      ended: () => { up({ playing: false, time: a.duration || 0 }); endedRef.current?.(); },
      timeupdate: () => up({ time: a.currentTime }),
      loadedmetadata: () => up({ dur: a.duration || 0, loading: false }),
      waiting: () => up({ loading: true }),
      canplay: () => up({ loading: false }),
      error: () => { if (a.src) up({ error: true, playing: false, loading: false }); },
    };
    Object.entries(h).forEach(([e, fn]) => a.addEventListener(e, fn));
    return () => {
      Object.entries(h).forEach(([e, fn]) => a.removeEventListener(e, fn));
      a.pause();
      clearInterval(fadeTimer.current);
    };
  }, []);

  const tryPlay = useCallback(() => {
    const a = ref.current;
    a.volume = 1;
    const p = a.play();
    if (p?.catch) p.catch(() => setSt((s) => ({ ...s, playing: false })));
  }, []);

  const fadeOut = useCallback(() => new Promise((resolve) => {
    const a = ref.current;
    clearInterval(fadeTimer.current);
    if (a.paused) return resolve();
    fadeTimer.current = setInterval(() => {
      a.volume = Math.max(0, a.volume - 0.2);
      if (a.volume <= 0.05) { clearInterval(fadeTimer.current); a.pause(); a.volume = 1; resolve(); }
    }, 30);
  }), []);

  const load = useCallback((url, autoplay) => {
    const a = ref.current;
    clearInterval(fadeTimer.current);
    a.pause();
    setSt({ playing: false, time: 0, dur: 0, error: !url, loading: !!url, src: url });
    if (!url) { a.removeAttribute("src"); return; }
    a.src = url;
    a.currentTime = 0;
    if (autoplay) tryPlay();
  }, [tryPlay]);

  const switchTo = useCallback(async (url, autoplay) => {
    await fadeOut();
    load(url, autoplay);
  }, [fadeOut, load]);

  const preload = useCallback((url) => {
    if (!url || preloads.current.has(url)) return;
    const p = new Audio();
    p.preload = "auto";
    p.src = url;
    preloads.current.set(url, p);
  }, []);

  const toggle = useCallback(() => {
    const a = ref.current;
    if (!a.src) return;
    if (a.paused) tryPlay(); else a.pause();
  }, [tryPlay]);

  const replay = useCallback(() => { ref.current.currentTime = 0; tryPlay(); }, [tryPlay]);
  const seek = useCallback((pct) => { const a = ref.current; if (a.duration) a.currentTime = pct * a.duration; }, []);
  const stop = useCallback(() => fadeOut(), [fadeOut]);
  const retry = useCallback(() => load(st.src, true), [load, st.src]);

  return { ...st, load, switchTo, preload, toggle, replay, seek, stop, retry, play: tryPlay };
}
